import {
  type Antibiotic,
  type Consultant,
  type CreateLabTestInput,
  type Department,
  type LabTest,
  type TestParameter,
  createLabTestSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { LabTestDetailsCard, LabTestPickerCard } from './LabTestFields';
import { initialLabTestValues, submittedLabTest } from './lab-test-form.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.labTests;

export function LabTestFormPage() {
  return (
    <ResourceFormShell<LabTest> path={PATH} noun="lab test">
      {({ id, record }) => <LabTestForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function LabTestForm({ id, record }: { id?: string | undefined; record?: LabTest | undefined }) {
  // The picker needs every parameter, not a page of them.
  const parameters = useAllRecords<TestParameter>({ path: RESOURCE.testParameters });
  const antibiotics = useAllRecords<Antibiotic>({ path: RESOURCE.antibiotics });
  const departments = useAllRecords<Department>({ path: RESOURCE.departments });
  const consultants = useAllRecords<Consultant>({
    path: RESOURCE.consultants,
    // Only doctors still signing. A retired one stays on the reports they
    // already signed, but must not be offerable on new work.
    extraQuery: { active: 'true' },
  });

  const { values, set } = useFormValues<CreateLabTestInput>(initialLabTestValues(record));

  const form = useResourceForm({ path: PATH, id, schema: createLabTestSchema, noun: 'Lab test' });

  return (
    <form onSubmit={form.handleSubmit(submittedLabTest(values))} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <LabTestDetailsCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          departments={departments.items}
          consultants={consultants.items}
        />

        {values.report_kind === 'culture' ? (
          <LabTestPickerCard
            title="Antibiotic panel"
            subtitle="Each selected antibiotic is put on every new report for this test"
            noun="antibiotics"
            items={antibiotics.items}
            selectedIds={values.antibiotic_ids}
            onSelectedIdsChange={(ids) => set('antibiotic_ids', ids)}
            error={form.errors.antibiotic_ids}
            isLoading={antibiotics.loading}
            loadError={antibiotics.error}
          />
        ) : (
          <LabTestPickerCard
            title="Parameters"
            subtitle="Each selected parameter becomes a row on the report"
            noun="parameters"
            items={parameters.items}
            selectedIds={values.parameter_ids}
            onSelectedIdsChange={(ids) => set('parameter_ids', ids)}
            error={form.errors.parameter_ids}
            isLoading={parameters.loading}
            loadError={parameters.error}
          />
        )}

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Create lab test'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
